// src/utils/getTurnCredentials.js
//
// Fetches short-lived TURN/STUN credentials from Metered.ca at call time.
//
// TURN is required for users behind strict NAT/firewalls.
// Falling back silently to STUN-only causes unpredictable failures:
// some peers connect (open NAT) while others fail with no clear reason.
//
// Correct behavior:
// - TURN available  → ICE servers include STUN + TURN (browser picks best path)
// - TURN unavailable → explicit turnStatus + reason; STUN fallback is still
//   returned for retry/diagnostics, but callers MUST NOT treat this as success

const STUN_FALLBACK = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const METERED_CREDENTIALS_URL =
  "https://skillsyncs.metered.live/api/v1/turn/credentials";

/** Normalize iceServers[].urls which may be a string or string[]. */
function collectUrls(entry) {
  if (!entry) return [];
  const raw = entry.urls ?? entry.url;
  if (!raw) return [];
  return (Array.isArray(raw) ? raw : [raw]).map(String);
}

export function classifyIceServers(iceServers) {
  const types = { stun: 0, turn: 0, turns: 0, other: 0 };
  for (const entry of iceServers || []) {
    for (const url of collectUrls(entry)) {
      const lower = url.toLowerCase();
      if (lower.startsWith("turns:")) types.turns += 1;
      else if (lower.startsWith("turn:")) types.turn += 1;
      else if (lower.startsWith("stun:")) types.stun += 1;
      else types.other += 1;
    }
  }
  return types;
}

function buildResult({ iceServers, turnStatus, reason = null, httpStatus = null }) {
  const servers = Array.isArray(iceServers) ? iceServers : STUN_FALLBACK;
  const iceServerTypes = classifyIceServers(servers);
  return {
    iceServers: servers,
    turnStatus,
    reason,
    httpStatus,
    iceServerCount: servers.length,
    iceServerTypes,
  };
}

/**
 * Fetch Metered ICE servers. Throws on HTTP / payload failures so callers
 * never mistake an auth/outage response for a valid TURN config.
 */
async function fetchMeteredIceServers(apiKey) {
  const response = await fetch(
    `${METERED_CREDENTIALS_URL}?apiKey=${encodeURIComponent(apiKey)}`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(
      `TURN credentials unavailable (${response.status}): ${
        errorData.error || "Unknown error"
      }`
    );
    err.httpStatus = response.status;
    throw err;
  }

  const iceServers = await response.json().catch(() => null);

  if (!Array.isArray(iceServers) || iceServers.length === 0) {
    throw new Error(
      "TURN credentials unavailable: Metered returned an empty or invalid ICE server list"
    );
  }

  const types = classifyIceServers(iceServers);
  // Metered should include relay endpoints. A STUN-only payload is not a
  // working TURN configuration even if HTTP 200.
  if (types.turn + types.turns === 0) {
    throw new Error(
      "TURN credentials unavailable: Metered response contained no TURN/TURNS servers"
    );
  }

  return { iceServers, httpStatus: response.status };
}

/**
 * Load ICE configuration for WebRTC.
 *
 * Never crashes the app: HTTP/auth failures become turnStatus "unavailable"
 * with a clear reason. STUN fallback servers are still attached so a later
 * retry or a deliberate continue path has a valid iceServers array — but
 * turnStatus must be checked by the caller before treating the call as healthy.
 */
export async function getTurnCredentials() {
  const apiKey = process.env.REACT_APP_METERED_API_KEY;

  if (!apiKey) {
    // TURN is required for users behind strict NAT/firewalls.
    // Falling back silently to STUN-only causes unpredictable failures.
    console.error(
      "[CALL] TURN unavailable: REACT_APP_METERED_API_KEY is not set"
    );
    return buildResult({
      iceServers: STUN_FALLBACK,
      turnStatus: "unavailable",
      reason: "REACT_APP_METERED_API_KEY is not set",
      httpStatus: null,
    });
  }

  try {
    const { iceServers, httpStatus } = await fetchMeteredIceServers(apiKey);
    return buildResult({
      iceServers,
      turnStatus: "available",
      reason: null,
      httpStatus,
    });
  } catch (error) {
    const reason = error?.message || "Unknown TURN credential error";
    // Do not silently pretend TURN is configured. Surface the exact reason
    // for diagnostics; STUN remains available only as an explicit fallback list.
    console.error(`[CALL] ${reason}`);
    return buildResult({
      iceServers: STUN_FALLBACK,
      turnStatus: "unavailable",
      reason,
      httpStatus: error?.httpStatus ?? null,
    });
  }
}

export { STUN_FALLBACK };
